/**
 * Delivery Availability Controller
 *
 * Prevents adding to cart when the selected variant has no inventory
 * at the main delivery location ("Bodega ecommerce", ID: 86054469914).
 *
 * Works with the variant metafield custom.available_for_delivery (boolean),
 * kept in sync by Shopify Flow.
 *
 * The Liquid snippet renders:
 *  - A <div id="DeliveryAvailability-{sectionId}-{productId}"> with data attrs
 *  - A <script data-delivery-availability-map> with a JSON map { variantId: bool }
 *
 * On variant change, ProductInfo re-fetches the section HTML, which re-renders
 * the snippet with fresh Liquid data. This JS also reads the JSON map for
 * instant client-side checks before the fetch completes.
 */
(function () {
  'use strict';

  const SOLDOUT_DELIVERY_TEXT = 'Agotado para envío';

  /**
   * Read the delivery availability map from the closest product-info context.
   * Returns an object like { "12345": true, "67890": false }
   */
  function getDeliveryMap(productInfoEl) {
    const script = productInfoEl.querySelector('[data-delivery-availability-map]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * Check if a variant ID is available for delivery.
   * Returns true if no map found (backwards-compatible default).
   */
  function isDeliveryAvailable(productInfoEl, variantId) {
    const map = getDeliveryMap(productInfoEl);
    if (!map || !(String(variantId) in map)) return true;
    return map[String(variantId)] === true;
  }

  /**
   * Apply delivery availability state to the product form UI.
   */
  function applyDeliveryState(productInfoEl, variantId, variant) {
    if (!productInfoEl || !variantId) return;

    const available = isDeliveryAvailable(productInfoEl, variantId);
    const deliveryEl = productInfoEl.querySelector('.delivery-availability');

    // Update the delivery availability message element
    if (deliveryEl) {
      deliveryEl.setAttribute('data-delivery-available', String(available));
      deliveryEl.setAttribute('data-variant-id', String(variantId));

      const existingMsg = deliveryEl.querySelector('.delivery-availability__message');
      if (!available) {
        if (!existingMsg) {
          const msg = document.createElement('p');
          msg.className = 'delivery-availability__message text-sm font-medium leading-tight';
          msg.style.cssText = 'color: var(--color-error, #c62828); margin-top: 0.5rem;';
          msg.textContent = 'Agotado para envío — solo disponible en tienda';
          deliveryEl.appendChild(msg);
        }
      } else if (existingMsg) {
        existingMsg.remove();
      }
    }

    // Update submit buttons
    const productForm = productInfoEl.querySelector('form[is="product-form"]');
    if (productForm) {
      updateSubmitButton(productForm, available, variant);
    }

    // Also update sticky form if present
    const sectionId = productInfoEl.getAttribute('data-section-id') ||
      productInfoEl.getAttribute('data-original-section-id');
    const productId = productInfoEl.getAttribute('data-product-id');
    const stickyForm = document.getElementById(`ProductStickyForm-${sectionId}-${productId}`);
    if (stickyForm) {
      updateSubmitButton(stickyForm, available, variant);
    }
  }

  /**
   * Update a form's submit button based on delivery availability.
   */
  function updateSubmitButton(form, deliveryAvailable, variant) {
    const submitBtn = form.querySelector('[type="submit"]');
    if (!submitBtn) return;

    if (!deliveryAvailable) {
      submitBtn.setAttribute('disabled', '');
      submitBtn.setAttribute('data-no-delivery', '');

      const textEl = submitBtn.querySelector('.btn-text > span') ||
        submitBtn.querySelector('.btn-text');
      if (textEl) {
        textEl.textContent = SOLDOUT_DELIVERY_TEXT;
      }
    } else {
      // Only remove our delivery block — don't override other disabled states
      submitBtn.removeAttribute('data-no-delivery');

      // If the variant itself is available, re-enable
      if (variant && variant.available) {
        submitBtn.removeAttribute('disabled');
        const textEl = submitBtn.querySelector('.btn-text > span') ||
          submitBtn.querySelector('.btn-text');
        if (textEl && textEl.textContent === SOLDOUT_DELIVERY_TEXT) {
          // Restore proper text — let the theme's own logic handle this
          // through the re-render cycle
        }
      }
    }
  }

  /**
   * Safety net: intercept form submission if delivery is not available.
   */
  function blockSubmitIfNoDelivery(event) {
    const form = event.target.closest('form[is="product-form"]') || event.target;
    if (!form || form.getAttribute('is') !== 'product-form') return;

    const productInfo = form.closest('product-info');
    if (!productInfo) return;

    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput) return;

    const variantId = variantInput.value;
    if (!isDeliveryAvailable(productInfo, variantId)) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const errorEl = form.querySelector('.product-form__error-message');
      if (errorEl) {
        errorEl.removeAttribute('hidden');
        errorEl.textContent = 'Este producto no está disponible para envío. Solo disponible para retiro en tienda.';
      }
      return false;
    }
  }

  /**
   * Listen for variant changes via the theme's pubsub system.
   * After ProductInfo re-renders the section HTML (which includes fresh
   * delivery-availability Liquid), we apply our JS state.
   */
  function init() {
    // Safety net: intercept submit on all product forms
    document.addEventListener('submit', function (e) {
      if (e.target.getAttribute && e.target.getAttribute('is') === 'product-form') {
        const result = blockSubmitIfNoDelivery(e);
        if (result === false) return;
      }
    }, true); // Use capture phase to run before the form's own handler

    // Listen for variant:change events dispatched by ProductInfo
    document.addEventListener('variant:change', function (e) {
      const variant = e.detail && e.detail.variant;
      if (!variant) return;

      // Find the closest product-info ancestor
      const form = e.target.closest ? e.target.closest('product-info') : null;
      const productInfo = form || e.target.closest('product-info');
      if (!productInfo) {
        // Try to find it from document — variant:change can bubble
        document.querySelectorAll('product-info').forEach(function (pi) {
          if (pi.contains(e.target)) {
            applyDeliveryState(pi, variant.id, variant);
          }
        });
        return;
      }

      applyDeliveryState(productInfo, variant.id, variant);
    });

    // Also subscribe to the theme's pubsub for variantChange
    if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
      theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.variantChange,
        function (event) {
          if (!event || !event.data) return;
          var data = event.data;
          var variant = data.variant;
          var sectionId = data.sectionId;
          var productId = data.productId;

          if (!variant) return;

          // Find the product-info element
          var productInfo = document.querySelector(
            'product-info[data-section-id="' + sectionId + '"][data-product-id="' + productId + '"]'
          ) || document.querySelector(
            'product-info[data-original-section-id="' + sectionId + '"][data-product-id="' + productId + '"]'
          );

          if (productInfo) {
            // Small delay to let the DOM update from updateSourceFromDestination
            setTimeout(function () {
              applyDeliveryState(productInfo, variant.id, variant);
            }, 50);
          }
        }
      );
    }

    // Apply initial state for all product-info elements on page
    document.querySelectorAll('product-info').forEach(function (productInfo) {
      var deliveryEl = productInfo.querySelector('.delivery-availability');
      if (deliveryEl) {
        var variantId = deliveryEl.getAttribute('data-variant-id');
        var available = deliveryEl.getAttribute('data-delivery-available') === 'true';
        if (!available && variantId) {
          // Re-apply to ensure buttons are properly set on initial load
          var form = productInfo.querySelector('form[is="product-form"]');
          if (form) {
            var submitBtn = form.querySelector('[type="submit"]');
            if (submitBtn && !submitBtn.hasAttribute('data-no-delivery')) {
              submitBtn.setAttribute('disabled', '');
              submitBtn.setAttribute('data-no-delivery', '');
              var textEl = submitBtn.querySelector('.btn-text > span') || submitBtn.querySelector('.btn-text');
              if (textEl) textEl.textContent = SOLDOUT_DELIVERY_TEXT;
            }
          }
        }
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
